import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
} from 'react-native';
import moment from 'moment';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import { useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
  bg:        '#0F172A',
  card:      '#1E293B',
  cardInner: '#0F172A',
  border:    'rgba(99,179,237,0.12)',
  text:      '#E2E8F0',
  textSub:   '#94A3B8',
  muted:     '#a8adb497',
  trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
  trackBg:   'rgba(0,0,0,0.03)',
};
const S = {
  accent:  '#38BDF8',
  ok:      '#34D399',
  err:     '#F87171',
  warn:    '#FB923C',
  replace: '#FBBF24',
  purple:  '#A78BFA',
  pink:    '#F472B6',
};

// APPROVAL 코드 → 상황 표시. 0=미승인 1=승인 2=거절
const STATUS_META: any = {
  0: { label: '미승인', color: S.warn },
  1: { label: '승인',   color: S.ok },
  2: { label: '거절',   color: S.err },
};

// 상단 탭 (기획서: 기본 미승인). value = getManagerList APPROVAL 파라미터('all'=전체)
const TABS: { label: string; value: any }[] = [
  { label: '미승인', value: 0 },
  { label: '승인',   value: 1 },
  { label: '거절',   value: 2 },
  { label: '전체',   value: 'all' },
];

// ─── 메인 컴포넌트 ────────────────────────────────────
export default function Management({ navigation }: any) {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [list, setList] = useState<any[]>([]);
  const [tab, setTab] = useState<any>(0);            // 현재 상황 탭 (기본 미승인)
  const [pendingCount, setPendingCount] = useState(0); // 미승인 건수(탭 배지용)
  const { dark, setDark } = useDarkMode();
  const [page, setPage] = useState(1);               // 현재 페이지(1-base)
  const [perPage, setPerPage] = useState(10);        // 페이지당 목록수
  const pageData = useMemo(
    () => list.slice((page - 1) * perPage, page * perPage),
    [list, page, perPage]
  );
  // 목록/탭이 바뀌면 첫 페이지로 (범위 벗어남 방지)
  useEffect(() => { setPage(1); }, [tab]);
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(list.length / perPage));
    if (page > totalPages) setPage(totalPages);
  }, [list, perPage]);

  useEffect(() => {
    if (!isFocused) return;
    getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
  }, [isFocused]);

  const T = dark ? DARK : LIGHT;

  // ─── 데이터 조회 ───
  const fetchByApproval = (approval: number): Promise<any[]> =>
    new Promise((resolve) => {
      Data.fetchData('group/getManagerList', { APPROVAL: approval },
        (result: any, count: any) => resolve(count > 0 ? (result || []) : []), true);
    });

  const getList = (value: any) => {
    if (value === 'all') {
      // 전체 = 미승인/승인/거절 합쳐서 최신순 (백엔드 무변경)
      Promise.all([0, 1, 2].map(fetchByApproval)).then((arr) => {
        const merged = ([] as any[]).concat(...arr)
          .sort((a, b) => new Date(b.REGDATE).getTime() - new Date(a.REGDATE).getTime());
        setList(merged);
      });
    } else {
      fetchByApproval(value).then(setList);
    }
  };

  const loadPendingCount = () => { fetchByApproval(0).then((r) => setPendingCount(r.length)); };

  useEffect(() => { getList(tab); loadPendingCount(); }, []);

  const onTab = (value: any) => { setTab(value); getList(value); };
  const refresh = () => { getList(tab); loadPendingCount(); };
  // 처리 후 해당 상태 탭으로 이동 (기획: 승인→승인 목록, 거절→거절 목록)
  const gotoTab = (value: any) => { setTab(value); getList(value); loadPendingCount(); };

  // ─── 인라인 작업 (팝업 제거) ───
  const doApprove = (row: any) => {
    if (!window.confirm(`${row.NAME || ''}(${row.AREA || ''}) 담당자를 승인할까요?`)) return;
    Data.fetchData('group/updateManagerApproval',
      { EMAIL: row.EMAIL, ID: row.ID, STATUS_CODE: 1, AREA: row.AREA },
      (result: any, count: any) => { if (count > 0) gotoTab(1); }, true);   // 승인 → 승인 목록으로 이동
  };
  const doReject = (row: any) => {
    if (!window.confirm(`${row.NAME || ''}(${row.AREA || ''}) 담당자를 거절할까요?`)) return;
    Data.fetchData('group/updateManagerApproval',
      { EMAIL: row.EMAIL, ID: row.ID, STATUS_CODE: 0, AREA: row.AREA },
      (result: any, count: any) => { if (count > 0) gotoTab(2); }, true);   // 거절 → 거절 목록으로 이동
  };
  const doRemove = (row: any) => {
    if (!window.confirm(`${row.NAME || ''}(${row.AREA || ''}) 담당자 정보를 완전히 삭제할까요? (되돌릴 수 없음)`)) return;
    Data.fetchData('group/removeManagerAllInfo',
      { EMAIL: row.EMAIL, ID: row.ID },
      (result: any, count: any) => { if (count > 0) refresh(); }, true);
  };

  // ─── 셀 렌더러 ───
  const StatusBadge = ({ approval }: any) => {
    const m = STATUS_META[approval] || STATUS_META[0];
    return (
      <View style={{ backgroundColor: m.color + '22', paddingHorizontal: s(8), paddingVertical: s(3), borderRadius: s(6) }}>
        <Text style={{ color: m.color, fontSize: s(12), fontWeight: '700' }}>{m.label}</Text>
      </View>
    );
  };

  const ActBtn = ({ label, color, onPress }: any) => (
    <TouchableOpacity
      onPress={onPress}
      style={{ backgroundColor: color, paddingHorizontal: s(12), height: s(28), borderRadius: s(6), justifyContent: 'center', alignItems: 'center', marginRight: s(6) }}
    >
      <Text style={{ color: '#fff', fontSize: s(12), fontWeight: '700' }}>{label}</Text>
    </TouchableOpacity>
  );

  const ActionCell = ({ row }: any) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: s(4) }}>
      {row.APPROVAL === 0 && (
        <>
          <ActBtn label="승인" color={S.ok} onPress={() => doApprove(row)} />
          <ActBtn label="거절" color={S.err} onPress={() => doReject(row)} />
        </>
      )}
      {row.APPROVAL === 1 && (
        <ActBtn label="거절" color={S.err} onPress={() => doReject(row)} />
      )}
      {row.APPROVAL === 2 && (
        <ActBtn label="승인" color={S.ok} onPress={() => doApprove(row)} />
      )}
      <TouchableOpacity
        onPress={() => doRemove(row)}
        style={{ width: s(28), height: s(28), borderRadius: s(6), justifyContent: 'center', alignItems: 'center', backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}
      >
        <Ionicons name="trash-outline" size={s(15)} color={T.muted} />
      </TouchableOpacity>
    </View>
  );

  const columns = [
    { id: 1, name: '구역', selector: (row: any) => row.AREA },
    { id: 2, name: '담당자', selector: (row: any) => row.NAME },
    { id: 3, name: '담당자 전화번호', selector: (row: any) => row.PHONE },
    { id: 4, name: '아이디', selector: (row: any) => row.ID },
    { id: 5, name: '이메일', selector: (row: any) => row.EMAIL, grow: 2 },
    { id: 6, name: '등록일', selector: (row: any) => moment(row.REGDATE).format('YYYY-MM-DD HH:mm:ss'), grow: 2 },
    { id: 7, name: '상황', grow: 0, width: s(80) + 'px', cell: (row: any) => <StatusBadge approval={row.APPROVAL} /> },
    { id: 8, name: '작업', grow: 0, width: s(180) + 'px', cell: (row: any) => <ActionCell row={row} /> },
  ];

  const tableStyles = {
    headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
    headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
    rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44) } },
    cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
    pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
      pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
    noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
  };

  const emptyText = tab === 1 ? '승인된 담당자가 없습니다.'
    : tab === 2 ? '거절된 기록이 없습니다.'
    : tab === 'all' ? '담당자 기록이 없습니다.'
    : '승인 요청이 없습니다.';

  return (
    <View style={[styles.container, { backgroundColor: T.bg }]}>
      <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
        {/* ─── 헤더 ─── */}
        <View style={styles.TopBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>담당자 승인관리</Text>
            {list?.length > 0 && (
              <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            {/* 상태 탭 */}
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: T.cardInner, borderRadius: s(10), padding: s(3), gap: s(2) }}>
              {TABS.map((t) => {
                const active = tab === t.value;
                return (
                  <TouchableOpacity
                    key={t.label}
                    onPress={() => onTab(t.value)}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: s(5), paddingHorizontal: s(12), height: s(30), borderRadius: s(8), backgroundColor: active ? S.accent : 'transparent' }}
                  >
                    <Text style={{ fontSize: s(13), fontWeight: '700', color: active ? '#fff' : T.textSub }}>{t.label}</Text>
                    {t.value === 0 && pendingCount > 0 && (
                      <View style={{ backgroundColor: active ? 'rgba(255,255,255,0.25)' : S.warn, paddingHorizontal: s(6), borderRadius: s(8), minWidth: s(16), alignItems: 'center' }}>
                        <Text style={{ fontSize: s(11), fontWeight: '700', color: '#fff' }}>{pendingCount}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
            <TouchableOpacity
              style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
              onPress={() => { navigation.navigate('Setting') }}
            >
              <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
            </TouchableOpacity>
          </View>
        </View>
        {/* ─── 테이블 ─── */}
        <DataTable
          columns={columns}
          data={pageData}
          customStyles={tableStyles}
          noDataComponent={
            <View style={{ alignItems: 'center', justifyContent: 'center', padding: s(30) }}>
              <Text style={{ fontSize: s(16), color: T.muted, textAlign: 'center' }}>{emptyText}</Text>
            </View>
          }
        />
        {list.length > 0 && (
          <TablePagination
            rowCount={list.length}
            perPage={perPage}
            page={page}
            onChangePage={setPage}
            onChangePerPage={(n: number) => { setPerPage(n); setPage(1); }}
            T={T}
            s={s}
            accent={S.accent}
          />
        )}
      </View>
    </View>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  container: {
    flex: 1,
    padding: s(24),
  },
  dataTable: {
    borderRadius: s(16),
    borderWidth: 0.5,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: s(20),
    elevation: 3,
    paddingTop: s(20),
    paddingHorizontal: s(10),
  },
  TopBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(6),
    paddingBottom: s(12),
  },
  iconBtn: {
    width: s(34),
    height: s(34),
    borderRadius: s(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
});
